import {
  Bell,
  Check,
  MessageSquare,
  Calendar,
  Heart,
  Users,
  HandHeart,
  Award,
  CheckCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  TabsContent,
  TabsTrigger,
  Tabs,
  TabsList,
} from "../components/ui/tabs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import BackButton from "../components/BackButton";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import notificationService from "../services/APIs/NotificationService";
import { formatDistanceToNow } from "date-fns";
import { Separator } from "../components/ui/separator";

const Notifications = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: notificationsData } = useQuery({
    queryKey: ["in-app-notifications-list"],
    queryFn: () => notificationService.getNotifications(),
    refetchOnWindowFocus: true,
  });

  const getIconForType = (title: string) => {
    const t = (title || "").toLowerCase();
    if (t.includes("sop")) return Calendar;
    if (t.includes("message")) return MessageSquare;
    if (t.includes("volunteer")) return HandHeart;
    if (t.includes("donation")) return Heart;
    if (t.includes("member") || t.includes("join")) return Users;
    if (t.includes("achievement")) return Award;
    return Bell;
  };

  const getTypeForTitle = (title: string) => {
    const t = (title || "").toLowerCase();
    if (t.includes("sop")) return "event";
    if (t.includes("message")) return "message";
    if (t.includes("volunteer")) return "volunteer";
    if (t.includes("donation")) return "donation";
    if (t.includes("member")) return "member";
    if (t.includes("achievement")) return "achievement";
    return "default";
  };

  const SOP_TEMPLATE = `New SOP "{name}" was created successfully. Please review the SOP and ensure all steps are properly configured. You can edit or assign it anytime from the dashboard.`;

  const SOP_TITLE_TEXT = "SOP Created Successfully";
  const MARK_AS_READ_TEXT = "Mark as read";
  const MARK_ALL_AS_READ_TEXT = "Mark all as read";

  const notifications = (notificationsData?.results || []).map((n: any) => {
    const type = getTypeForTitle(n.title);
    let description = n.message;
    let title = n.title;

    if (type === "event" && n.extra?.sop_title) {
      description = SOP_TEMPLATE.replace("{name}", n.extra.sop_title);
      title = SOP_TITLE_TEXT;
    }

    return {
      id: n.id,
      type: type,
      icon: getIconForType(n.title),
      title: title,
      description: description,
      time: n.created_on ? formatDistanceToNow(new Date(n.created_on), { addSuffix: true }) : "Unknown time",
      read: n.is_read,
      avatar: null,
      action: () => {
        if (n.extra?.sop_id) {
          navigate("/sop-library");
        }
      }
    };
  });

  const unreadCount = notifications.filter((n: any) => !n.read).length;

  const markAsRead = async (id: string) => {
    // Optimistic update
    queryClient.setQueryData(["in-app-notifications-list"], (oldData: any) => {
      if (!oldData?.results) return oldData;
      return {
        ...oldData,
        results: oldData.results.map((n: any) =>
          n.id === id ? { ...n, is_read: true } : n
        ),
      };
    });
    queryClient.setQueryData(
      ["in-app-notifications", "unread-count"],
      (oldCount: number | undefined) => Math.max(0, (oldCount || 1) - 1)
    );
    // Real API call
    try {
      await notificationService.markAsRead(id);
    } catch {
      // Revert on failure
      queryClient.invalidateQueries({ queryKey: ["in-app-notifications-list"] });
      queryClient.invalidateQueries({ queryKey: ["in-app-notifications", "unread-count"] });
    }
  };

  const markAllAsRead = async () => {
    // Optimistic update
    queryClient.setQueryData(["in-app-notifications-list"], (oldData: any) => {
      if (!oldData?.results) return oldData;
      return {
        ...oldData,
        results: oldData.results.map((n: any) => ({ ...n, is_read: true })),
      };
    });
    queryClient.setQueryData(["in-app-notifications", "unread-count"], 0);
    // Real API call
    try {
      await notificationService.markAllAsRead();
    } catch {
      // Revert on failure
      queryClient.invalidateQueries({ queryKey: ["in-app-notifications-list"] });
      queryClient.invalidateQueries({ queryKey: ["in-app-notifications", "unread-count"] });
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case "message":
        return "text-blue-500";
      case "event":
        return "text-purple-500";
      case "volunteer":
        return "text-green-500";
      case "donation":
        return "text-red-500";
      case "member":
        return "text-orange-500";
      case "achievement":
        return "text-yellow-500";
      default:
        return "text-primary";
    }
  };

  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read);
  const { data: headerUnreadCount = 0 } = useQuery({
    queryKey: ["in-app-notifications", "unread-count"],
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    staleTime: 30000,
  });

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                <div>
                  <h1 className="text-lg font-bold text-primary md:text-xl">
                    Notifications
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    {unreadCount > 0
                      ? `${unreadCount} unread`
                      : "All caught up!"}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() =>
                  navigate(CitycollabNavigationRoutes.NOTIFICATIONS)
                }
              >
                <Bell className="h-5 w-5" />
                {headerUnreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                    {headerUnreadCount > 9 ? "9+" : headerUnreadCount}
                  </span>
                )}
              </Button>
              {unreadCount > 0 && (
                <Button variant="outline" size="sm" onClick={markAllAsRead}>
                  <CheckCheck className="mr-2 h-4 w-4" />
                  {MARK_ALL_AS_READ_TEXT}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 md:py-8">
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="mb-6 grid w-full grid-cols-3">
            <TabsTrigger value="all">
              All
              {unreadCount > 0 && (
                <Badge variant="default" className="ml-2 h-5 min-w-5 px-1.5">
                  {unreadCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="unread">Unread</TabsTrigger>
            <TabsTrigger value="read">Read</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-2">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              notifications.map(notification => (
                <Card
                  key={notification.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${!notification.read ? "border-primary/50 bg-primary/5" : ""
                    }`}
                  onClick={() => {
                    notificationService.readNotification(notification.id);
                    markAsRead(notification.id);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3">
                        {notification.avatar ? (
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={notification.avatar} />
                            <AvatarFallback>
                              {notification.title
                                .split(" ")
                                .map((n: string) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full bg-muted ${getNotificationColor(notification.type)}`}
                          >
                            <notification.icon className="h-5 w-5" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <h3
                              className={`text-sm font-semibold ${!notification.read ? "text-foreground" : "text-muted-foreground"}`}
                            >
                              {notification.title}
                            </h3>
                            {!notification.read && (
                              <div className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="rounded-lg border border-border/50 bg-muted/20 p-3">
                          <p className="whitespace-pre-line text-sm text-muted-foreground">
                            {notification.description}
                          </p>
                        </div>
                        <Separator className="bg-border/50" />
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {notification.time}
                          </span>
                          {!notification.read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={e => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                            >
                              <Check className="mr-1 h-3 w-3" />
                              {MARK_AS_READ_TEXT}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="unread" className="space-y-2">
            {unreadNotifications.length === 0 ? (
              <div className="py-12 text-center">
                <CheckCheck className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">No unread notifications</p>
              </div>
            ) : (
              unreadNotifications.map(notification => (
                <Card
                  key={notification.id}
                  className="cursor-pointer border-primary/50 bg-primary/5 transition-all hover:shadow-md"
                  onClick={() => {
                    notificationService.readNotification(notification.id);
                    markAsRead(notification.id);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3">
                        {notification.avatar ? (
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={notification.avatar} />
                            <AvatarFallback>
                              {notification.title
                                .split(" ")
                                .map((n: string) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full bg-muted ${getNotificationColor(notification.type)}`}
                          >
                            <notification.icon className="h-5 w-5" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-foreground">
                              {notification.title}
                            </h3>
                            <div className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="rounded-lg border border-border/50 bg-muted/20 p-3">
                          <p className="whitespace-pre-line text-sm text-muted-foreground">
                            {notification.description}
                          </p>
                        </div>
                        <Separator className="bg-border/50" />
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {notification.time}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={e => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                          >
                            <Check className="mr-1 h-3 w-3" />
                            {MARK_AS_READ_TEXT}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="read" className="space-y-2">
            {readNotifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">No read notifications</p>
              </div>
            ) : (
              readNotifications.map(notification => (
                <Card
                  key={notification.id}
                  className="cursor-pointer transition-all hover:shadow-md"
                  onClick={() => {
                    notificationService.readNotification(notification.id);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3">
                        {notification.avatar ? (
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={notification.avatar} />
                            <AvatarFallback>
                              {notification.title
                                .split(" ")
                                .map((n: string) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full bg-muted ${getNotificationColor(notification.type)}`}
                          >
                            <notification.icon className="h-5 w-5" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-semibold text-muted-foreground">
                            {notification.title}
                          </h3>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="rounded-lg border border-border/50 bg-muted/20 p-3">
                          <p className="whitespace-pre-line text-sm text-muted-foreground">
                            {notification.description}
                          </p>
                        </div>
                        <Separator className="bg-border/50" />
                        <span className="text-xs text-muted-foreground">
                          {notification.time}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Notifications;
